import {
	fetchAuthMe,
	googleLogin,
	googleMobileLogin,
	login,
	logout,
	refreshToken,
	register,
	requestPasswordReset,
	resendVerificationByEmail,
	resendVerificationEmail,
	resetPassword,
	verifyEmail,
} from "@bodhistreams/api/src/authApi";

/** Server error code for a correct password on an account that never verified its email. */
export const EMAIL_NOT_VERIFIED = "EMAIL_NOT_VERIFIED";

/**
 * True when a failed login/refresh was refused only because the email is unverified.
 * Login screens use this to swap the form for the verification notice.
 * @param {*} error axios error
 */
export const isEmailNotVerifiedError = (error) =>
	error?.response?.data?.error === EMAIL_NOT_VERIFIED;

import { setLoggingOut } from "@bodhistreams/api/src/axiosInstanceV1";
import useActivityStore from "@bodhistreams/stores/src/activityStore";
import useUIStore from "@bodhistreams/stores/src/uiStore";
import { flushClientLogger, logClientEvent } from "@bodhistreams/utils";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { message } from "antd";

/**
 * Query keys factory for auth queries
 */
export const authQueryKeys = {
	all: ["auth"],
	me: () => [...authQueryKeys.all, "me"],
};

// ========== Queries ==========

/**
 * Lightweight auth identity query — reads JWT claims only (no DB).
 * Returns: { user_id, email, first_name, last_name, is_admin, membership_name, membership_id }
 * Always enabled — returns null when no token is present.
 */
export const useAuthMe = () => {
	return useQuery({
		queryKey: authQueryKeys.me(),
		queryFn: async () => {
			try {
				return await fetchAuthMe();
			} catch (e) {
				if (e?.response?.status === 401) return null;
				throw e;
			}
		},
		staleTime: 5 * 60 * 1000,
		gcTime: 30 * 60 * 1000,
		retry: (failureCount, error) => {
			if (error?.response?.status === 401 || error?.response?.status === 403) {
				return false;
			}
			return failureCount < 3;
		},
	});
};

// ========== Mutations ==========

/**
 * Hook for logging in with email/password
 * @returns {Object} React Query mutation result
 */
export const useLogin = () => {
	const queryClient = useQueryClient();
	const hideLoginModal = useUIStore((state) => state.hideLoginModal);

	return useMutation({
		mutationFn: ({ email, password }) => login(email, password),
		onSuccess: () => {
			// Cookies are set by the server — just refresh auth state
			queryClient.invalidateQueries({ queryKey: authQueryKeys.me() });

			hideLoginModal();
			message.success(`Welcome back!`);
			useActivityStore.getState().track({ kind: "auth.login" });
		},
		onError: (error) => {
			// The login screens render a dedicated panel for this case; a toast on
			// top of it would just be noise.
			if (isEmailNotVerifiedError(error)) return;
			const errorMessage =
				error.response?.data?.message || "Login failed. Please try again.";
			message.error(errorMessage);
		},
	});
};

/**
 * Hook for registering a new account
 * @returns {Object} React Query mutation result
 */
export const useRegister = () => {
	const queryClient = useQueryClient();
	const hideRegisterModal = useUIStore((state) => state.hideRegisterModal);

	return useMutation({
		mutationFn: (userData) => register(userData),
		onSuccess: (data) => {
			// Registration no longer opens a session: the account stays locked until
			// the email is verified, so there is no auth state to refresh here.
			queryClient.removeQueries({ queryKey: authQueryKeys.me() });

			hideRegisterModal();
			message.success(
				data?.message ||
					"Account created. Check your email for the verification link, then sign in.",
			);
			useActivityStore.getState().track({ kind: "auth.register" });
		},
		onError: (error) => {
			const errorMessage =
				error.response?.data?.message || "Registration failed";
			message.error(errorMessage);
		},
	});
};

/**
 * Hook for refreshing an access token
 * @returns {Object} React Query mutation result
 */
export const useRefreshToken = () => {
	return useMutation({
		mutationFn: (token) => refreshToken(token),
	});
};

/**
 * Hook for verifying an email address
 * @returns {Object} React Query mutation result
 */
export const useVerifyEmail = () => {
	return useMutation({
		mutationFn: (token) => verifyEmail(token),
		onSuccess: () => {
			message.success(
				"Email verified successfully! You can now enjoy all features.",
			);
		},
		onError: (error) => {
			const errorMessage =
				error.response?.data?.message || "Email verification failed";
			message.error(errorMessage);
		},
	});
};

/**
 * Hook for resending a verification email
 * @returns {Object} React Query mutation result
 */
export const useResendVerificationEmail = () => {
	return useMutation({
		mutationFn: (userId) => resendVerificationEmail(userId),
		onSuccess: () => {
			message.success("Verification email sent! Please check your inbox.");
		},
		onError: (error) => {
			const errorMessage =
				error.response?.data?.message || "Failed to send verification email";
			message.error(errorMessage);
		},
	});
};

/**
 * Hook for resending a verification email by address (no user id needed).
 * The server answers 200 for unknown addresses too, and 429 when a resend
 * happened in the last minute.
 * @returns {Object} React Query mutation result
 */
export const useResendVerificationByEmail = () => {
	return useMutation({
		mutationFn: (email) => resendVerificationByEmail(email),
		onSuccess: (data) => {
			message.success(
				data?.message || "Verification email sent. Please check your inbox.",
			);
		},
		onError: (error) => {
			const errorMessage =
				error.response?.data?.message || "Failed to send verification email";
			message.error(errorMessage);
		},
	});
};

/**
 * Hook for logging in with Google (web OAuth code)
 * @returns {Object} React Query mutation result
 */
export const useGoogleLogin = () => {
	const queryClient = useQueryClient();
	const hideLoginModal = useUIStore((state) => state.hideLoginModal);

	return useMutation({
		mutationFn: (code) => googleLogin(code),
		onSuccess: () => {
			// Cookies are set by the server — just refresh auth state
			queryClient.invalidateQueries({ queryKey: authQueryKeys.me() });
			hideLoginModal();

			message.success(`Welcome! You've successfully logged in with Google.`);
		},
		onError: (error) => {
			const errorMessage =
				error.response?.data?.message ||
				"Google login failed. Please try again.";
			message.error(errorMessage);
		},
	});
};

/**
 * Hook for logging in with Google (mobile ID token)
 * @returns {Object} React Query mutation result
 */
export const useGoogleMobileLogin = () => {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (idToken) => googleMobileLogin(idToken),
		onSuccess: () => {
			// Cookies are set by the server — just refresh auth state
			queryClient.invalidateQueries({ queryKey: authQueryKeys.me() });
		},
	});
};

/**
 * Hook for requesting a password reset email
 * @returns {Object} React Query mutation result
 */
export const useRequestPasswordReset = () => {
	return useMutation({
		mutationFn: (email) => requestPasswordReset(email),
	});
};

/**
 * Hook for resetting a password
 * @returns {Object} React Query mutation result
 */
export const useResetPassword = () => {
	return useMutation({
		mutationFn: ({ token, newPassword }) => resetPassword(token, newPassword),
		onSuccess: () => {
			message.success(
				"Password reset successfully! You can now login with your new password.",
			);
		},
		onError: (error) => {
			const errorMessage =
				error.response?.data?.message || "Failed to reset password";
			message.error(errorMessage);
		},
	});
};

/**
 * Hook for logging out — clears server-side cookies and local state
 * @returns {Object} React Query mutation result
 */
export const useLogout = () => {
	const queryClient = useQueryClient();

	return useMutation({
		mutationFn: async () => {
			// Latch OFF the silent-refresh interceptor before we clear cookies, so a
			// racing /user/* 401 can't re-mint the session and log the user back in.
			// Callers outside the interceptor check this too — see getLoggingOut().
			setLoggingOut(true);
			// Mark both ends of the logout window. Without these the client side of
			// a resurrected session is invisible: the backend sees a refresh land
			// just after a logout, but nothing says what the tab was doing.
			logClientEvent({
				kind: "auth_logout_started",
				level: "info",
				message: "logout requested",
			});
			// Stop any in-flight auth query from refetching mid-logout.
			await queryClient.cancelQueries({ queryKey: authQueryKeys.me() });
			return logout();
		},
		onSuccess: () => {
			logClientEvent({
				kind: "auth_logout_completed",
				level: "info",
				message: "logout completed",
			});
			useActivityStore.getState().track({ kind: "auth.logout" });
			useActivityStore.getState().resetSession();
			queryClient.removeQueries({ queryKey: authQueryKeys.me() });
			message.success("You have been logged out successfully");
		},
		onError: (error) => {
			// A failed logout is the dangerous case: cookies may survive and the
			// refresh token may not have been revoked, so the session can come
			// back. Log it at error level rather than silently cleaning up.
			logClientEvent({
				kind: "auth_logout_failed",
				level: "error",
				status: error?.response?.status || 0,
				message: error?.response?.data?.message || "logout request failed",
			});
			// Even if server call fails, clear query state
			useActivityStore.getState().resetSession();
			queryClient.removeQueries({ queryKey: authQueryKeys.me() });
		},
		onSettled: () => {
			// Ship the logout events now — the caller navigates away immediately
			// after this, and a pending 4s batch timer would never fire.
			flushClientLogger();
		},
	});
};
